import { useEffect, useState } from 'react';
import { AppShell } from '../components/AppShell';
import { useUserOrg } from '../lib/org';
import { LoadingIndicator } from '../components/LoadingIndicator';
import {
  listSubcontractors,
  listAllTrainingRecords,
  listTrainingPeople,
  listPlant,
  type TrainingPerson,
} from '../lib/db';

interface ExpiryItem {
  category: string;
  label: string;
  date: string;          // ISO yyyy-mm-dd
  daysAway: number;
}

const CARD_LABELS: Record<string, string> = {
  cscs: 'CSCS',
  first_aid: 'First Aid',
  asbestos: 'Asbestos awareness',
  pasma: 'PASMA',
  ipaf: 'IPAF MEWP',
  cpcs_telehandler: 'CPCS Telehandler',
  abrasive_wheels: 'Abrasive Wheels',
};

function daysFromNow(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.ceil(ms / 86400000);
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function RegisterExpiries() {
  const { org } = useUserOrg();
  const [items, setItems] = useState<ExpiryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!org) return;
    (async () => {
      try {
        const [subs, plant, ppl, recs] = await Promise.all([
          listSubcontractors(org.org_id),
          listPlant(org.org_id),
          listTrainingPeople(org.org_id),
          listAllTrainingRecords(org.org_id),
        ]);
        const peopleById: Record<string, TrainingPerson> = {};
        for (const p of ppl) peopleById[p.id] = p;

        const out: ExpiryItem[] = [];
        for (const s of subs) {
          if (s.pl_insurance_expiry) out.push({ category: 'Insurance', label: `${s.company_name} · Public Liability`, date: s.pl_insurance_expiry, daysAway: daysFromNow(s.pl_insurance_expiry) });
          if (s.el_insurance_expiry) out.push({ category: 'Insurance', label: `${s.company_name} · Employers Liability`, date: s.el_insurance_expiry, daysAway: daysFromNow(s.el_insurance_expiry) });
          if (s.pi_insurance_expiry) out.push({ category: 'Insurance', label: `${s.company_name} · Professional Indemnity`, date: s.pi_insurance_expiry, daysAway: daysFromNow(s.pi_insurance_expiry) });
        }
        for (const r of recs) {
          if (!r.expires_on) continue;
          const personName = peopleById[r.person_id]?.full_name ?? 'Unknown';
          out.push({
            category: 'Training',
            label: `${personName} · ${CARD_LABELS[r.card_type] ?? r.card_type}`,
            date: r.expires_on,
            daysAway: daysFromNow(r.expires_on),
          });
        }
        for (const p of plant) {
          if (p.loler_date) {
            // Assume LOLER renewal is 12 months from last date
            const next = new Date(p.loler_date);
            next.setFullYear(next.getFullYear() + 1);
            const iso = next.toISOString().slice(0, 10);
            out.push({ category: 'Plant', label: `${p.name} · next LOLER`, date: iso, daysAway: daysFromNow(iso) });
          }
          if (p.pat_date) {
            const next = new Date(p.pat_date);
            next.setFullYear(next.getFullYear() + 1);
            const iso = next.toISOString().slice(0, 10);
            out.push({ category: 'Plant', label: `${p.name} · next PAT`, date: iso, daysAway: daysFromNow(iso) });
          }
        }

        out.sort((a, b) => a.daysAway - b.daysAway);
        setItems(out);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [org]);

  const overdue = items.filter((i) => i.daysAway < 0);
  const within30 = items.filter((i) => i.daysAway >= 0 && i.daysAway <= 30);
  const within90 = items.filter((i) => i.daysAway > 30 && i.daysAway <= 90);

  function row(it: ExpiryItem, urgency: 'overdue' | 'soon' | 'later') {
    const cls = urgency === 'overdue' ? 'badge-danger' : urgency === 'soon' ? 'badge-warn' : 'badge-neutral';
    const label = urgency === 'overdue' ? `${Math.abs(it.daysAway)}d overdue` : `${it.daysAway}d`;
    return (
      <li key={`${it.category}-${it.label}-${it.date}`} className="person-item">
        <div>
          <p className="person-name">{it.label}</p>
          <p className="person-meta">{it.category} · expires {fmtDate(it.date)}</p>
        </div>
        <span className={`badge ${cls}`}>{label}</span>
      </li>
    );
  }

  return (
    <AppShell title="Insurances & expiries" back="/registers">
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
          Everything across your registers that's expiring or already expired. Updated live as you change dates.
        </p>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      {loading ? (
        <LoadingIndicator />
      ) : (
        <>
          <h3 className="section-title">Overdue <span className="count-badge">{overdue.length}</span></h3>
          {overdue.length === 0 ? (
            <div className="card"><p style={{ margin: 0, color: 'var(--success)' }}>Nothing overdue. ✓</p></div>
          ) : (
            <ul className="person-list">{overdue.map((it) => row(it, 'overdue'))}</ul>
          )}

          <h3 className="section-title" style={{ marginTop: 22 }}>Next 30 days <span className="count-badge">{within30.length}</span></h3>
          {within30.length === 0 ? (
            <div className="card"><p style={{ margin: 0, color: 'var(--ink-soft)' }}>Nothing expiring in the next 30 days.</p></div>
          ) : (
            <ul className="person-list">{within30.map((it) => row(it, 'soon'))}</ul>
          )}

          <h3 className="section-title" style={{ marginTop: 22 }}>30–90 days <span className="count-badge">{within90.length}</span></h3>
          {within90.length === 0 ? (
            <div className="card"><p style={{ margin: 0, color: 'var(--ink-soft)' }}>Nothing in this window.</p></div>
          ) : (
            <ul className="person-list">{within90.map((it) => row(it, 'later'))}</ul>
          )}
        </>
      )}
    </AppShell>
  );
}
